import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import 'dayjs/locale/en-gb';
import { DAYJS_LOCALE, NUMBER_LOCALE } from '@/lib/locale';

dayjs.extend(isoWeek);
dayjs.locale(DAYJS_LOCALE);

// Month abbreviations for every label in the app. Intl's en-GB short month
// is "Sept" while dayjs and the period picker say "Sep": one spelling here.
export const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-09" (or a full ISO date) → "Sep". */
export function monthShort(yyyyMm: string): string {
  return MONTH_SHORT[Number(yyyyMm.split('-')[1]) - 1] ?? yyyyMm;
}

/**
 * Person name for display. Source exports mix "alessia maiolani" and
 * "DANIELE CALLEGARO"; all-lower / all-upper names are title-cased, names
 * already in mixed case ("Luca De Bernardi") are left as typed.
 */
export function personName(name: string | null | undefined): string {
  const n = (name ?? '').trim();
  if (!n || (n !== n.toLowerCase() && n !== n.toUpperCase())) return n;
  return n.toLowerCase().replace(/(^|[\s'’-])([a-zà-ÿ])/g, (_, sep: string, c: string) => sep + c.toUpperCase());
}

// Lower-case joiners in company names ("De Longhi", "Banca di Roma").
const NAME_PARTICLES = new Set(['de', 'di', 'da', 'del', 'della', 'dei', 'la', 'le', 'lo', 'il', 'e', 'and', 'of', 'the', 'von', 'van', 'y']);
// Legal forms kept in their usual spelling.
const LEGAL_FORMS: Record<string, string> = { gmbh: 'GmbH', spa: 'SpA', srl: 'Srl', sas: 'SAS', snc: 'Snc', inc: 'Inc', ltd: 'Ltd', llc: 'LLC', plc: 'PLC', bv: 'BV', nv: 'NV', ag: 'AG', sa: 'SA', sl: 'SL' };

/**
 * Tokens that stay upper-case when a name or code is title-cased. Company
 * acronyms come from the client lists of every tenant (all-caps in the
 * exports: "ABB", "BNL", "GSK"); the rest are terms of the trade that show
 * up in project codes ("AMC", "SEO", "DTC"). Two-letter tokens are treated
 * as acronyms without being listed (LG, HP, BI, HR, UK), see TWO_LETTER_WORDS.
 * Anything else of three letters or more is a word: ACER → Acer, RED → Red.
 */
export const ACRONYMS = new Set([
  // Company names
  'ABB', 'ACI', 'ACS', 'ADR', 'ADS', 'AMG', 'AMPP', 'ANAC', 'ANCI', 'ASI', 'ATM',
  'AVM', 'AXA', 'BAT', 'BCC', 'BHS', 'BKT', 'BNL', 'BPER', 'BPM', 'BRT', 'CAI',
  'CDP', 'CEG', 'CIF', 'CIR', 'CNH', 'CNP', 'CTP', 'DAX', 'DAZN', 'DXC', 'ESCP',
  'ESN', 'FHSF', 'FIGC', 'FMCC', 'FSI', 'GEA', 'GHD', 'GPN', 'GSK', 'GSR', 'GTT',
  'HBO', 'IAF', 'IDM', 'IEG', 'IFOA', 'IGP', 'IGT', 'IMA', 'IPC', 'LCA', 'LED',
  'LLP', 'LVMH', 'MPC', 'MVN', 'NEM', 'NEN', 'OGR', 'OTB', 'PBC', 'PKB', 'PTA',
  'RCS', 'RINA', 'RSG', 'SACE', 'SBS', 'SCM', 'SCT', 'SDA', 'SEB', 'SIU', 'SNCF',
  'SSC', 'TDR', 'TIM', 'TML', 'TNB', 'TTI', 'UFI', 'UGL', 'VGR', 'VSG', 'WTL',
  'XCC', 'YOOX',
  // Trade terms in project codes
  'ADV', 'AMC', 'AMZ', 'API', 'B2B', 'B2C', 'BTL', 'CMS', 'CRM', 'CRO', 'CTV',
  'DEM', 'DSP', 'DTC', 'ERP', 'FMCG', 'KPI', 'OTT', 'PIM', 'PPC', 'PRO', 'ROI',
  'SAP', 'SEA', 'SEM', 'SEO', 'SMM', 'SMS', 'SSP', 'UGC', 'USA',
]);

// Two-letter tokens that are words, not acronyms.
const TWO_LETTER_WORDS = new Set(['go', 'me', 'up', 'my', 'we', 'on', 'in', 'to', 'at', 'by', 'or', 'an', 'no', 'so']);

/** Casing for one token of an all-caps / all-lower name. */
function caseToken(tok: string, first: boolean): string {
  const lower = tok.toLowerCase();
  const bare = lower.replace(/[.,()]/g, '');
  const up = bare.toUpperCase();
  if (LEGAL_FORMS[bare]) return tok.replace(new RegExp(bare, 'i'), LEGAL_FORMS[bare]);
  if (NAME_PARTICLES.has(bare)) return first ? lower.replace(/[a-zà-ÿ]/, (c) => c.toUpperCase()) : lower;
  if (/\d/.test(tok) || ACRONYMS.has(up) || (bare.length === 2 && !TWO_LETTER_WORDS.has(bare))) return tok.toUpperCase();
  return lower.replace(/(^|['’&.])([a-zà-ÿ])/g, (_, sep: string, c: string) => sep + c.toUpperCase());
}

/**
 * Client / company name for display. Source exports carry "GROUPE SEB",
 * "ACER", "DE LONGHI" next to "Ikram Jerradi": all-caps and all-lower names
 * are title-cased, names already in mixed case are left as typed. Acronyms
 * (ACRONYMS, any two-letter token, anything with a digit: 3M) stay upper,
 * joiners go lower (DE → De at the start, di mid-name). Display only: the
 * raw name stays the key for filters, joins and exports.
 */
export function displayClient(name: string | null | undefined): string {
  const n = (name ?? '').trim();
  if (!n || (n !== n.toLowerCase() && n !== n.toUpperCase())) return n;
  let first = true;
  return n
    .split(/(\s+|-|\/)/)
    .map((tok) => {
      if (!/[a-zA-Zà-ÿÀ-ß]/.test(tok)) return tok;
      const out = caseToken(tok, first);
      first = false;
      return out;
    })
    .join('');
}

// One number convention everywhere (design review §2): € in front, "."
// thousands, "," decimals; compact figures in upper-case K up to 10M, then
// M with one decimal (€7.763K · €550K · €12,4M). Exact values in tables and
// tooltips, compact in KPIs, cards and chart axes.
const numFull = new Intl.NumberFormat(NUMBER_LOCALE, {
  maximumFractionDigits: 0,
});

const decFormatters = new Map<number, Intl.NumberFormat>();
function decFormatter(digits: number): Intl.NumberFormat {
  let f = decFormatters.get(digits);
  if (!f) {
    f = new Intl.NumberFormat(NUMBER_LOCALE, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
    decFormatters.set(digits, f);
  }
  return f;
}

function signed(abs: string, value: number, withSign: boolean): string {
  if (value < 0) return `-${abs}`;
  if (withSign && value > 0) return `+${abs}`;
  return abs;
}

/** Exact euro amount: "€1.411.757", "-€795.634". */
export function formatEur(value: number): string {
  return signed(`€${numFull.format(Math.abs(Math.round(value)))}`, Math.round(value), false);
}

export function formatNum(value: number): string {
  return numFull.format(value);
}

/** Fixed-decimal number in the app convention ("12,5"). */
export function formatDecimal(value: number, digits = 1): string {
  return decFormatter(digits).format(value);
}

/** Compact magnitude without the symbol: "7.763K", "550K", "12,4M", "940".
 * K up to 10M on purpose: the business reads figures in thousands and one
 * scale keeps cards comparable; M only once K would need five digits. */
export function compactEur(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 10_000_000) return `${decFormatter(1).format(value / 1_000_000)}M`;
  if (abs >= 1_000) return `${numFull.format(Math.round(value / 1_000))}K`;
  return numFull.format(Math.round(value));
}

/** Compact euro with the symbol in front: "€7.763K", "-€795K", "€12,4M". */
export function formatEurCompact(value: number, options: { withSign?: boolean } = {}): string {
  return signed(`€${compactEur(Math.abs(value))}`, value, Boolean(options.withSign));
}

/** compactEur split into figure and unit ("7.763" / "K") so the unit can be
 * set smaller and lighter than the number it qualifies; Figure puts the €
 * in front, as formatEur does. */
export function splitCompactEur(value: number): { num: string; unit: string } {
  const c = compactEur(value);
  const suffix = c.endsWith('M') || c.endsWith('K') ? c.slice(-1) : '';
  return { num: suffix ? c.slice(0, -1) : c, unit: suffix };
}

/** Euro chart-axis tick: "€1,2M", "€200K", "€0". Axes switch to M at 1M
 * (not 10M like compactEur): a tick reads best short. */
export function formatAxisValue(value: number): string {
  if (value === 0) return '€0';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return signed(`€${decFormatter(1).format(abs / 1_000_000)}M`, value, false);
  return formatEurCompact(value);
}

interface FormatMoneyOptions {
  withSign?: boolean;
  placeholder?: string;
}

/** Exact euro for KPIs and tooltips: zero stays "€0". */
export function formatMoney(
  value: number | null | undefined,
  options: FormatMoneyOptions = {},
): string {
  const { withSign = false, placeholder = '—' } = options;
  if (value == null) return placeholder;
  const rounded = Math.round(value);
  return signed(`€${numFull.format(Math.abs(rounded))}`, rounded, withSign);
}

/** Money without the currency symbol, for table cells whose column header
 * carries the unit ("Budget €"). Same sign rules as formatMoney; a zero
 * reads as the placeholder ("—") in tables, per the review. */
export function formatAmount(
  value: number | null | undefined,
  options: FormatMoneyOptions = {},
): string {
  const { withSign = false, placeholder = '—' } = options;
  if (value == null) return placeholder;
  const rounded = Math.round(value);
  if (rounded === 0) return placeholder;
  return signed(numFull.format(Math.abs(rounded)), rounded, withSign);
}

export function formatCompact(value: number | null | undefined): string {
  if (value == null) return '—';
  return compactEur(value);
}

export function formatLastUpdate(iso: string | null | undefined, kind: string): string {
  if (!iso) return '';
  const d = dayjs(iso);
  return `${kind} updated on ${d.format('D MMM YYYY')} at ${d.format('HH:mm')}`;
}

export function formatDateTime(iso: string | null | undefined, fallback = '—'): string {
  if (!iso) return fallback;
  return dayjs(iso).format('D MMM YYYY HH:mm');
}

export function formatHours(value: number | null | undefined, placeholder = ''): string {
  if (value == null) return placeholder;
  return `${numFull.format(Math.round(value))}h`;
}

export function formatPct(
  value: number | null | undefined,
  options: { digits?: number; multiply?: boolean; placeholder?: string } = {},
): string {
  const { digits = 0, multiply = true, placeholder = '—' } = options;
  if (value == null || Number.isNaN(value)) return placeholder;
  const pct = multiply ? value * 100 : value;
  return `${decFormatter(digits).format(pct)}%`;
}

/**
 * Share of a total (0–1 by default): one decimal under 10% so neighbouring
 * ranks stay distinguishable ("3,5%" vs "3,1%"), whole numbers above
 * ("27%"). Zero reads as the placeholder.
 */
export function formatShare(
  value: number | null | undefined,
  options: { multiply?: boolean; placeholder?: string } = {},
): string {
  const { multiply = true, placeholder = '—' } = options;
  if (value == null || Number.isNaN(value) || value === 0) return placeholder;
  const pct = multiply ? value * 100 : value;
  if (Math.abs(pct) < 0.05) return pct > 0 ? '<0,1%' : '>-0,1%';
  return `${decFormatter(Math.abs(pct) < 10 ? 1 : 0).format(pct)}%`;
}

/**
 * Whole-number percentage that never rounds a real value down to "0%":
 * under 0,5% reads "<1%", exactly zero reads as the placeholder.
 */
export function formatPctFloor(
  value: number | null | undefined,
  options: { multiply?: boolean; placeholder?: string } = {},
): string {
  const { multiply = true, placeholder = '—' } = options;
  if (value == null || Number.isNaN(value) || value === 0) return placeholder;
  const pct = multiply ? value * 100 : value;
  if (pct > 0 && pct < 0.5) return '<1%';
  return `${decFormatter(0).format(pct)}%`;
}

/** Hours chart-axis tick: "14.000", "800", "0". */
export function formatAxisHours(value: number): string {
  return numFull.format(Math.round(value));
}

/** Count chart-axis tick (users, pageviews): "1.250", "40", "0". */
export function formatAxisCount(value: number): string {
  return numFull.format(Math.round(value));
}

export function formatPpDelta(
  value: number | null | undefined,
  options: { digits?: number; multiply?: boolean; placeholder?: string } = {},
): string {
  const { digits = 0, multiply = true, placeholder = '—' } = options;
  if (value == null || Number.isNaN(value)) return placeholder;
  if (value === 0) return placeholder;
  const pp = multiply ? value * 100 : value;
  const sign = pp > 0 ? '+' : '';
  return `${sign}${decFormatter(digits).format(pp)}pp`;
}

export function deltaColor(value: number | null | undefined): 'success.main' | 'error.main' | 'text.secondary' {
  if (value == null || value === 0) return 'text.secondary';
  return value > 0 ? 'success.main' : 'error.main';
}

/**
 * Snapshot label with ISO week context, e.g. "W23 · 9 Jun".
 * Snapshots are weekly uploads, so the week number is the operator's primary
 * mental handle ("compare W22 to W23"); the calendar date disambiguates.
 */
export function formatSnapshotLabel(iso: string | null | undefined, fallback = '—'): string {
  if (!iso) return fallback;
  const d = dayjs(iso);
  if (!d.isValid()) return fallback;
  return `W${d.isoWeek()} · ${d.format('D MMM')}`;
}
